import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { LatestImage } from '@/@types/latestImages';

import {
  DEFAULT_BRIGHTNESS_VALUE,
  DEFAULT_CONTRAST_VALUE,
} from '../../../../constants';
import {
  patientDownloadableNotesAtom,
  patientDownloadableSelectedTreatmentsAtom,
} from '../../casePresentation/atoms';

export const isHQEnabledAtom = atom<boolean | undefined>(undefined);
export const isOpacityInvertedAtom = atom(false);
export const contrastValueAtom = atom(DEFAULT_CONTRAST_VALUE);
export const brightnessValueAtom = atom(DEFAULT_BRIGHTNESS_VALUE);
export const hideFindingsAtom = atom(false);
export const showSegmentationAtom = atomWithStorage<boolean | undefined>(
  'showSegmentation',
  undefined,
);
export const showSegmentationFromEmbeddedAtom = atom(false);
export const shouldPersistUserModificationsAtom = atom(false);

// Custom atom that resets notes when preview closes
export const isPreviewOpenAtom = atom(
  (get) => get(isPreviewOpenAtomInternal),
  (get, set, newValue: boolean) => {
    set(isPreviewOpenAtomInternal, newValue);

    // When closing preview, reset notes to default state
    if (newValue === false) {
      set(patientDownloadableNotesAtom, '');
      set(patientDownloadableSelectedTreatmentsAtom, []);
    }
  },
);

// Internal atom to store the actual preview state
const isPreviewOpenAtomInternal = atom(false);

export const previewModeAtom = atom<'print' | 'qrCode' | undefined>('print');
export const isExpandedAtom = atom(false);
export const previewDisplayDeviceAtom = atom<'mobile' | 'desktop' | undefined>(
  'desktop',
);

// Compare images atom for preview dialog
export const compareImagesAtom = atom<{
  leftImage: LatestImage | null;
  rightImage: LatestImage | null;
  leftVisitId?: number;
  rightVisitId?: number;
} | null>(null);

// Full screen state atom
export const isFullScreenOpenAtom = atom(false);

// QR Code state atoms
export interface QRCodeResponseProps {
  link: string;
  qrCode: string;
  accessCode: number | null;
}

export const qrCodeDataAtom = atom<QRCodeResponseProps | null>(null);
export const qrCodeLoadingAtom = atom(false);
export const showQRCodeAtom = atom(false);
export const qrCodeErrorAtom = atom<string | null>(null);

// Image quality assessment navigation atoms
export const imageQualityAssessmentPatientIdsAtom = atom<string[]>([]);
export const imageQualityAssessmentCurrentIndexAtom = atom<number | null>(null);
export const imageQualityAssessmentOverlayOpenAtom = atom(false);
export const imageQualityAssessmentOverlayPositionAtom = atomWithStorage<{
  x: number;
  y: number;
}>('imageQualityAssessmentOverlayPosition', { x: 80, y: 80 });
